import React, { useState, useEffect, useContext, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  updateToolsSettings,
  alignWidgets,
  arrangeWidget,
  cloneWidgets,
  clonePages,
  updateWidgetLayout,
  updateWidgetProperty,
  deleteWidgets,
  actionGroup,
  selectWidget
} from "actions/editor";

import cx from "classnames";
import _ from "lodash";
import Draggable from 'react-draggable';
import { Button, Popover, OverlayTrigger } from "react-bootstrap";

import { PAGE , ANCHOR, SECTION_COLUMN, MENU, MENU_CONTAINER, HOTSPOT, TEXT, FORM_FIELD, OTP_INPUT, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_BOX, FORM_SUBMIT, FORM_CHECK_LIST, IMAGE, VIDEO, SHAPE, LINE, BUTTON, FORM, BASIC_TEXT, HTML, RECTANGLE, LIVE_ELEMENT, CAROUSEL, ASSET, CONTENT, SECTION, SEEK, IFRAME, LIVE_ELEMENT_CONTAINER, OTP_FORM_SUBMIT, OTP_RESEND, CONTENT_FILTER_CONTAINER, CONTENT_SEARCH, MENU_DRAGGABLE_CONTAINER, SLIDE } from "constants/widgetTypes";

import { getBoundingRectForWidgets, getBoundingRectFromLayout,getBoundingRectForWidget, getBoundingRectForWidgetAlignment } from '../../helper/layoutManager';
import { EditorContext } from "components/Editor";
import Tooltip from "components/Tooltip";
import Portal from 'components/Portal';
import { WidgetRegistry } from '../../widgets/registry';
import {
  blockClipboardOperation,
  blockDeleteOperation,
  blockPasteOperation,
  isPasteBlockedDuringAiImageGeneration,
} from 'components/Editor/utils/aiImageEditGuard';
import { isWidgetLocked, useSelectedWidgetSelector, useWidgetChildrenSelector } from "components/Editor/hooks/useEditorSession";

import styles from "./tools.module.scss";

import { GROUP } from 'constants/widgetTypes';
import { FREE } from 'constants/widgetDisplayTypes';
import { VIDEO as VIDEO_ASSET_TYPE } from 'constants/widgetAssetTypes';
import StoryboardContext from "components/Storyboard/context";
import { SEGMENT_STORYBOARDS, supportsFrameEditorFeatures } from 'constants/storyboardType';

const isAssetHorizontalEditRestricted = (widget, breakpoint) =>
  widget?.type === ASSET && (
    _.get(widget, 'properties.desktop.display', PAGE) !== FREE ||
    (breakpoint === 'mobile' && _.get(widget, 'properties.desktop.type') !== VIDEO_ASSET_TYPE)
  );

export default function Tools({ root, style }) {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const allWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`));
  const selectedWidgets = useSelectedWidgetSelector(editorSession);

  const isAllWidgetLocked = selectedWidgets.every(widget =>
    isWidgetLocked(allWidgets, widget, breakpoint)
  );

  const [position, setPosition] = useState({
    x: 100,
    y: 300
  });

  useLayoutEffect(() => {
    calculateDialogPosition();
  }, []);

  const calculateDialogPosition = () => {
    var targetEl = document.querySelector(`#${editorSession} .editor_widget_scroll_container`);
    if(!targetEl) {
      return;
    }
    
    const rect = targetEl.getBoundingClientRect();

    const x = 40;
    const y = rect.top + 40;

    setPosition({
      x: x,
      y: y
    });
  }

  const handlePositionChange = (e, ui) => {
    setPosition((position) => {
      return {
        x: position.x + ui.deltaX,
        y: position.y + + ui.deltaY
      };
    });
  }

  const handleClose = () => {
    dispatch(updateToolsSettings(editorSession, {
      toolbar: false
    }));
  }

  return (
    <Portal>
      <Draggable handle={`.${styles.dragHandle}`} bounds='parent' position={position} onDrag={handlePositionChange}>
        <div className={styles.tools} style={style}>
          <header className={styles.dragHandle}>
            <div className={styles.title}>&nbsp;</div>
            <i className={cx(styles.draggabel, 'cleverstory-drag')} />
            <div className={styles.actions}>
              <Button className={styles.close} onClick={handleClose}>
                <i className='cleverstory-close' />
              </Button>
            </div>
          </header>
          <main>
            <CreationTool isAllWidgetLocked={isAllWidgetLocked}/>
            <div className={styles.seperator}></div>
            <div className={cx(styles.group, styles.buttonGrid)}>
              <ArrangeTool isAllWidgetLocked={isAllWidgetLocked}/>
              <AlignTool storyboardType={storyboardType} isAllWidgetLocked={isAllWidgetLocked}/>
              <MatchSizeTool isAllWidgetLocked={isAllWidgetLocked}/>
              <DistributeEquallyTool isAllWidgetLocked={isAllWidgetLocked}/>
            </div>
            <div className={styles.seperator}></div>
            <RotationTool isAllWidgetLocked={isAllWidgetLocked}/>
            <div className={styles.seperator}></div>
            <SizeTool isAllWidgetLocked={isAllWidgetLocked}/>
            <div className={styles.seperator}></div>
            <PositionTool isAllWidgetLocked={isAllWidgetLocked}/>
          </main>
        </div>
      </Draggable>
    </Portal>
  );
}

const CreationTool = ({isAllWidgetLocked}) => {
  const dispatch = useDispatch();
  const editorContext = useContext(EditorContext);
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  const editorSession = editorContext.editorSession.id;
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const supportsFrames = supportsFrameEditorFeatures(entityType, storyboardType);
  const activeFrameId = useSelector(state => {
    const selectedPage = _.get(state, `${editorSession}.present.selectedPage`, null);
    const activeSegment = _.get(state, `${editorSession}.present.activeSegment`, null);
    return selectedPage !== activeSegment ? activeSegment : selectedPage;
  });

  const [state, setState] = useState({
    copy: false,
    paste: true,
    clone: false,
    deletable: false
  });

  const parent = useSelector(state => _.get(state, `${editorSession}.present.interactive.active`));
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const selection = useSelector(state => _.get(state, `${editorSession}.present.selected`));
  const selectedFrame = useSelector(state => _.get(state, `${editorSession}.present.selectedPage`));
  const allWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`));
  const selectedWidgets = useSelectedWidgetSelector(editorSession);
  const pages = useSelector((state) => {
      const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
      var pages = widgets.filter((item) => item.type === PAGE);
      pages = _.orderBy(pages, "order", "desc");
      return pages;
  });

  useEffect(() => {
    _calculateToolState();
  }, [selection, allWidgets]);

  const _calculateToolState = () => {
    var copy = _.size(selection) > 0;
    var paste = !isPasteBlockedDuringAiImageGeneration(allWidgets);
    var clone = _.size(selection)  > 0;
    var deletable = _.size(selection) > 0;

    selectedWidgets.forEach(widget => {
      const sectionUid = widget.type === SECTION_COLUMN ? widget.parent : null;
      const columnCount = sectionUid ? allWidgets.filter((w) => w.parent === sectionUid).length : 0;
      if (columnCount === 1){
        deletable = false;
      }

      const template = _.get(widget, 'template');

      if(template) {
        copy = false;
        clone = false;
        deletable = false;
      }

      const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
      const isSegmentPage = widget.type === PAGE && supportsFrames;
      copy = copy && (isSegmentPage || (_.get(moveableConfig, 'cloneable', true) && !_.includes([SECTION_COLUMN, HOTSPOT], widget.type)));
      clone = clone && (isSegmentPage || _.get(moveableConfig, 'cloneable', true));
      deletable = deletable && (isSegmentPage
        ? _.size(pages) >= 2 && (!_.get(widget, 'properties.desktop.visible', true) || pages.filter(p => _.get(p, 'properties.desktop.visible', true)).length >= 2)
        : _.get(moveableConfig, 'deletable', true));
    });

    if (selectedFrame && _.size(selection) == 0 && supportsFrames) {
      copy = true;
      clone = true;
      const currentPage = pages.find(p => p.uid === selectedFrame);
      const visiblePageCount = pages.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
      deletable = _.size(pages) >= 2 && currentPage && (!_.get(currentPage, 'properties.desktop.visible', true) || visiblePageCount >= 2);
    }

    const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
    const allFramesSelected =
      supportsFrames &&
      selectedPageCount > 0 &&
      selectedPageCount === pages.length;

    if (allFramesSelected) {
      deletable = false;
    }

    setState({
      copy: copy,
      paste: paste,
      clone: clone,
      deletable: deletable
    });
  }

  /**
   * Actions Handlers
  */
  const handleCopy = (e) => {
    if (blockClipboardOperation(selectedWidgets)) {
      return;
    }
    editorContext.editorSession.eventBus.emit('copy');
  }
    
  const handlePaste = async (event) => {
    if (blockPasteOperation(allWidgets)) {
      return;
    }

    const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
  
    const files = [];
    let hasFiles = false;
  
    try {
      const items = clipboardData?.items;
      if (items) {
        for (const item of items) {
          if (item.kind === "file") {
            const file = item.getAsFile();
  
            if (file && !file.type.startsWith("text/")) {
              files.push(file);
              hasFiles = true;
            }
          }
        }
      }
  
      if (hasFiles) {
        editorContext.editorSession.eventBus.emit("pasteMedia", { files });
        return;
      }
    } catch (err) {
      console.warn("Error reading clipboardData items:", err);
    }
  
    try {
      const clipboardItems = await navigator.clipboard.read();
  
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (!type.startsWith("text/")) {
            const blob = await item.getType(type);
            const ext = type.split("/")[1] || "bin";
  
            const file = new File([blob], `pasted-file.${ext}`, { type });
            files.push(file);
            hasFiles = true;
          }
        }
      }
  
      if (hasFiles) {
        editorContext.editorSession.eventBus.emit("pasteMedia", { files });
        return;
      }
    } catch (clipboardError) {
      console.log("Clipboard API access failed:", clipboardError);
    }
  
    const selected = selectedWidgets?.[0];
    if (selected && selected.type === TEXT) {
      const editor = editorContext.editorRegistry[selected.uid];
      if (editor) {
        const isEditing = editorContext.isTextEditing[selected.uid];

        if (isEditing) {
          try {
            const text = await navigator.clipboard.readText();
            const pasteEvent = new Event("paste", { bubbles: true, cancelable: true });
            pasteEvent.clipboardData = {
              getData: (type) => (type === "text/plain" ? text : ""),
              types: ["text/plain"]
            };
            editor.getBody().dispatchEvent(pasteEvent);
            return;
          } catch (err) {
            console.error("Clipboard paste failed:", err);
          }
        }
      }
    }
    editorContext.editorSession.eventBus.emit("paste");
  };


  const handleClone = (e) => {
    if (blockClipboardOperation(selectedWidgets)) {
      return;
    }

    let selectedPages = supportsFrames
      ? selectedWidgets.filter(widget => widget.type === PAGE)
      : [];

    if (selectedPages.length === 0 && selectedWidgets.length === 0 && selectedFrame && supportsFrames) {
      selectedPages = pages.filter(page => page.uid === selectedFrame);
    }

    if (selectedPages.length > 0 && activeFrameId) {
      dispatch(clonePages(
        editorSession,
        selectedPages.map(page => page.uid),
        activeFrameId,
        'segment'
      ));
    }

    const widgetsByParent = selectedWidgets
      .filter(widget => widget.type !== PAGE)
      .reduce((acc, widget) => {
        const { parent, uid } = widget;

        if (!parent) {
          return acc;
        }

        if (!acc[parent]) {
          acc[parent] = [];
        }
        acc[parent].push(uid);

        return acc;
      }, {});

    Object.entries(widgetsByParent).forEach(([parent, widgetIds]) => {
      dispatch(cloneWidgets(editorSession, parent, widgetIds));
    });
  };
  

  const handleDelete = (e) => {
    if (blockDeleteOperation(selectedWidgets)) {
      return;
    }

    const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
    const allFramesSelected =
      supportsFrames &&
      selectedPageCount > 0 &&
      selectedPageCount === pages.length;

    if (allFramesSelected) {
      return;
    }

    if (selectedFrame && _.size(selection) == 0 && supportsFrames) {
      const currentPage = pages.find(p => p.uid === selectedFrame);
      const visiblePageCount = pages.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
      if (
        _.size(pages) >= 2 &&
        currentPage &&
        (!_.get(currentPage, 'properties.desktop.visible', true) || visiblePageCount >= 2)
      ) {
        const currentPage = pages.find(p => p.uid === selectedFrame);
        const visiblePageCount = pages.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
        if (
          _.size(pages) >= 2 &&
          currentPage &&
          (!_.get(currentPage, 'properties.desktop.visible', true) || visiblePageCount >= 2)
        ) {
          dispatch(deleteWidgets(editorSession, [selectedFrame]));
        }
      }
      return;
    }

    const widgets = selectedWidgets
    .filter(widget => !isWidgetLocked(allWidgets, widget, breakpoint))
    .map(widget => widget.uid);
    dispatch(deleteWidgets(editorSession, widgets));
  }

  return (
    <div className={cx(styles.group, styles.buttonGrid)}>
      <span>
        <ToolAction
          icon='cleverstory-copy'
          content='Copy'
          disabled={!state.copy}
          onClick={handleCopy}
        />
      </span>
      <span>
        <ToolAction
          icon='cleverstory-paste'
          content='Paste'
          disabled={!state.paste}
          onClick={handlePaste}
        />
      </span>
      <span>
        <ToolAction
          icon='cleverstory-clone'
          content='Clone'
          disabled={!state.clone}
          onClick={handleClone}
        />
      </span>
      <span>
        <ToolAction
          icon='cleverstory-trash'
          content='Delete'
          disabled={ selectedFrame && _.size(selection) == 0  ? !state.deletable : !state.deletable || isAllWidgetLocked}
          onClick={handleDelete}
        />
      </span>
    </div>
  )
}

const RotationTool = ({isAllWidgetLocked}) => {
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const widget = useEditableWidget(editorSession);
  
  var disabled = true;
  if(widget){
    const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
    disabled = !_.get(moveableConfig, 'rotatable', true);

    if (isAllWidgetLocked) {
      disabled = true;
    }
  }
  
  const handleRotationChanged = (value) => {
    if (!widget) {
      return;
    }

    const processed = parseRotationValue(value);
    const moveableManager = editorContext.editorSession.moveableManager.current;
    moveableManager.request("rotatable", { rotate: processed }, true);
  }

  const parseRotationValue = (value) => {
    value = parseFloat(value);
    value = Math.round(value);

    var processed = value;

    if (value >= 0) {
      const count = Math.floor((value / 360), 0);
      processed = value - 360 * count;
    } else {
      const count = Math.ceil(Math.abs(value / 360));
      processed = 360 * count + value;
    }

    processed = processed || 0;
    return processed;
  }

  var rotation = _.get(widget, `properties.${breakpoint}.layout.transform.rotate`) || 0;
  rotation = parseRotationValue(rotation);

  return (
    <>
      <div className={styles.group}>
        <div className={cx(styles.input, styles.rotation)}>
          <i className="cleverstory-angle" />
          <NumericInput
            disabled={disabled}
            value={rotation}
            onChange={handleRotationChanged}
          />
        </div>
      </div>
    </>
  );
}

const PositionTool = ({isAllWidgetLocked}) => {
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const moveableManager = editorContext.editorSession.moveableManager.current;
  const draggable = _.get(moveableManager, 'props.draggable') || false;

  const widgets = useEditableWidgets(editorSession);

  const [rect, setRect] = useState(() => getBoundingRectForWidgets(editorSession, widgets, breakpoint));
  const x = _.get(rect, 'x', '0');
  const y = _.get(rect, 'y', '0');

  useLayoutEffect(() => {
    const newRect = getBoundingRectForWidgets(editorSession, widgets, breakpoint);
    let rectX = _.get(newRect, "x", "0");
    let rectY = _.get(newRect, "y", "0");

    // Only update state if rect has actually changed
    if (!_.isEqual(rect, newRect)) {
      setRect(newRect);
    }

    // Update DOM elements synchronously
    const elementX = document.getElementById("tools-position-x");
    const elementY = document.getElementById("tools-position-y");

    if (elementX && elementY) {
      elementX.value = Math.floor(rectX);
      elementY.value = Math.floor(rectY);
    }
  }, [widgets, breakpoint, editorSession]);

  const disabled = _.size(widgets) == 0 || !draggable || isAllWidgetLocked;
  const xDisabled = disabled || widgets.some(widget => isAssetHorizontalEditRestricted(widget, breakpoint));

  const handleXChange = (value) => {
    if (isNaN(value) || value === '' || value === null || value === undefined) {
      return;
    }
    
    const numValue = Number(value);
    const diff = numValue - x;
  
    if (moveableManager) {
      moveableManager.request("draggable", { deltaX: diff, deltaY: 0 }, true);
    }
  }

  const handleYChange = (value) => {
    if (isNaN(value) || value === '' || value === null || value === undefined) {
      return;
    }
    
    const numValue = Number(value);
    const diff = numValue - y;
  
    if (moveableManager) {
      moveableManager.request("draggable", { deltaX: 0, deltaY: diff }, true);
    }
  }

  return (
    <div className={styles.group}>
      <div className={styles.title}>Position</div>
      <div className={styles.input}>
        <span>X</span>
        <NumericInput
          id={"tools-position-x"}
          disabled={xDisabled}
          min={-499770}
          max={499770}
          value={Math.floor(x)}
          onChange={handleXChange}
        />
      </div>
      <div className={styles.input}>
        <span>Y</span>
        <NumericInput
          id={"tools-position-y"}
          disabled={disabled}
          min={0}
          max={499770}
          value={Math.floor(y)}
          onChange={handleYChange}
        />
      </div>
    </div>
  );
}

const SizeTool = ({isAllWidgetLocked}) => {
  const dispatch = useDispatch();
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  let min = 10;
  const moveableManager = editorContext.editorSession.moveableManager.current;
  const selected = useSelector(state => _.get(state, `${editorSession}.present.selected`));
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const activePageSection = useSelector(state => _.get(state, `${editorSession}.present.interactive.active`));
  const allWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []));

  const widgets = useEditableWidgets(editorSession);
  const rect = moveableManager ? moveableManager.getRect() : {};
  const width = _.get(rect, 'width', '');
  const height = _.get(rect, 'height', '');
  let selectedWidget = widgets.filter(widget => widget.uid === _.head(selected));
  const externalWidgets = [MENU];
  const resizableWidget = moveableManager ? moveableManager.props.resizable : false;
  const widthRestriction = selectedWidget && _.includes(externalWidgets, _.head(selectedWidget)?.type);
  const heightRestriction = selectedWidget && _.includes([MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, SEEK], _.head(selectedWidget)?.type);

  const assetWidthRestricted = widgets.some(widget => isAssetHorizontalEditRestricted(widget, breakpoint));
  const disabled = _.size(widgets) == 0 || isAllWidgetLocked || !resizableWidget || widthRestriction;
  const disabledHeight = _.size(widgets) == 0 || isAllWidgetLocked || !resizableWidget || heightRestriction;

  // Slide card mode: with nothing selected, edit the active resizable subslide's
  // height directly (flipbook, mobile, index >= 1). The first slide stays fixed.
  const slideWidgets = _.orderBy(
    allWidgets.filter(w => w.type === SLIDE && _.get(w, 'properties.desktop.visible')),
    'order', 'asc'
  );
  const activeSlideIndex = slideWidgets.findIndex(w => w.uid === activePageSection);
  const activeSlide = slideWidgets[activeSlideIndex];
  const slideCardMode =
    _.size(selected) === 0 &&
    storyboardType === 'FLIP_BOOK' &&
    breakpoint === 'mobile' &&
    activeSlideIndex >= 1;

  const slideHeight = parseInt(_.get(activeSlide, `properties.${breakpoint}.layout.height`)) || 600;
  const slideWidth = parseInt(_.get(activeSlide, `properties.${breakpoint}.layout.width`)) || '';

  const handleWidthChange = (value) => {
    if (slideCardMode) {
      return; // slide card width is fixed
    }

    const diff = value - width;

    if(selectedWidget && _.includes(externalWidgets, _.head(selectedWidget).type)) {
      return
    }

    if (moveableManager) {
      moveableManager.request("resizable", {
        direction: [1, 0],
        deltaWidth: diff,
        deltaHeight: 0
      }, true);
    }
  }

  const handleHeightChange = (value) => {
    if (slideCardMode) {
      const newHeight = Math.max(600, Number(value) || 0);
      const layout = _.get(activeSlide, `properties.${breakpoint}.layout`) || {};
      dispatch(
        updateWidgetProperty(editorSession, activePageSection, breakpoint, {
          layout: {
            ...layout,
            height: `${newHeight}px`,
          },
        })
      );
      return;
    }

    const diff = value - height;

    if (moveableManager) {
      moveableManager.request("resizable", {
        direction: [0, 1],
        deltaWidth: 0,
        deltaHeight: diff
      }, true);
    }
  }

  const widthValue = slideCardMode ? slideWidth : width;
  const heightValue = slideCardMode ? slideHeight : height;
  const widthDisabled = slideCardMode ? true : (disabled || assetWidthRestricted);
  const heightDisabled = slideCardMode ? false : disabledHeight;
  const heightMin = slideCardMode ? 600 : min;

  return (
    <div className={styles.group}>
      <div className={styles.title}>Size (px)</div>
      <div className={styles.input}>
        <span>W</span>
        <NumericInput
          disabled={widthDisabled}
          min={min}
          max={499770}
          step={1}
          value={widthValue}
          onChange={handleWidthChange}
        />
      </div>
      <div className={styles.input}>
        <span>H</span>
        <NumericInput
          disabled={heightDisabled}
          min={heightMin}
          max={499770}
          step={1}
          value={heightValue}
          onChange={handleHeightChange}
        />
      </div>
    </div>
  );
}

const ArrangeTool = ({isAllWidgetLocked}) => {
  const dispatch = useDispatch();
  const toolIgnoreTypes = [CONTENT_FILTER_CONTAINER, CONTENT_SEARCH]

  const editorContext = useContext(EditorContext);

  const editorSession = editorContext.editorSession.id;
  const widget = useEditableWidget(editorSession);

  const buttonState = useSelector(state => {
    if (!widget) {
      return {
        back: false,
        front: false
      };
    }

    var widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    widgets = widgets.filter(item => item.parent === widget.parent);
    widgets = _.orderBy(widgets, 'order', 'desc');

    const index = widgets.findIndex(item => item.uid === widget.uid);
    const total = _.size(widgets);

    return {
      back: index < total - 1,
      front: index > 0,
      index: index,
      total: total
    };
  });

  const id = _.get(widget, 'uid');

  const sendBack = () => {
    if (!buttonState.back) {
      return;
    }

    dispatch(arrangeWidget(editorSession, widget, buttonState.total - 1));
  }

  const sendBackward = () => {
    if (!buttonState.back) {
      return;
    }

    dispatch(arrangeWidget(editorSession, widget, buttonState.index + 1));
  }

  const bringForward = () => {
    if (!buttonState.front) {
      return;
    }

    dispatch(arrangeWidget(editorSession, widget, buttonState.index - 1));
  }

  const bringFront = () => {
    if (!buttonState.front) {
      return;
    }

    dispatch(arrangeWidget(editorSession, widget, 0));
  }

  const popover = (
    <Popover
      id="popover-arrange-tool"
      className={styles.arrangeToolPopup}
    >
      <Popover.Body>
        <button disabled={!buttonState.front} className={styles.action} onClick={bringForward}>
          <i className="cleverstory-bring-forward" />
          <span>Bring Forward</span>
        </button>
        <button disabled={!buttonState.front} className={styles.action} onClick={bringFront}>
          <i className="cleverstory-bring-front" />
          <span>Bring to Front</span>
        </button>
        <button disabled={!buttonState.back} className={styles.action} onClick={sendBackward}>
          <i className="cleverstory-send-backward" />
          <span>Bring Backward</span>
        </button>
        <button disabled={!buttonState.back} className={styles.action} onClick={sendBack}>
          <i className="cleverstory-send-back" />
          <span>Send to back</span>
        </button>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="right-start"
      rootClose
      overlay={popover}
    >
      <span className={cx({[styles.actionDisabled]:widget == null})}>
        <ToolAction
          icon='cleverstory-send-back'
          content='Arrange'
          disabled={widget == null || toolIgnoreTypes.includes(widget.type) || isAllWidgetLocked}
        />
      </span>
    </OverlayTrigger>
  );
}

const AlignTool = ({storyboardType, isAllWidgetLocked}) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const allWidgets = useSelector((state) => _.get(state, `${editorSession}.present.widgets`, null));
  const widgets = useEditableWidgets(editorSession);
  
  const handleAlignment = (type, direction) => {
    dispatch(alignWidgets(editorSession, breakpoint, type, direction, storyboardType, allWidgets));
  }

  const widgetsAllowOnly = [
    TEXT, BUTTON, FORM_FIELD, OTP_INPUT, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST,
    FORM_CHECK_LIST, FORM_CHECK_BOX, FORM_SUBMIT, OTP_FORM_SUBMIT, OTP_RESEND, IMAGE, VIDEO, SHAPE, LINE, GROUP, 
    FORM, BASIC_TEXT, HTML, RECTANGLE, LIVE_ELEMENT, CAROUSEL, SECTION, ASSET, CONTENT, MENU, SEEK, IFRAME
  ];
  const nonHorizontalWidgets = [CAROUSEL, SECTION, ASSET, CONTENT, MENU, SEEK];

  const disabled = () => {
    if (_.size(widgets) < 1) {
      return true;
    }
    return widgets.some(item => !widgetsAllowOnly.includes(item.type));
  }

  const disableHorizontalAlign = () => {
    if (_.size(widgets) < 1) {
      return true;
    }

    return widgets.some(item => {
      if (item.type === ASSET && item.properties.desktop.display === 'FREE') {
        return false;
      }

      if (item.type === CAROUSEL && item.properties.desktop.free) {
        return false;
      }

      return nonHorizontalWidgets.includes(item.type);
    });
  };

  const disableVerticalAlign = () => {
    if (_.size(widgets) < 1) {
      return true;
    }
  
    return widgets.some(item => {
      const parentElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${item?.parent}`);
      
      return parentElement && parentElement.getAttribute('data-widget-type') === LIVE_ELEMENT_CONTAINER;
    });
  };
  
  const popover = (
    <Popover
      id="popover-arrange-tool"
      className={styles.arrangeToolPopup}
    >
      <Popover.Body>
        <button className={cx(styles.action, { [styles.actionDisabled]: disableHorizontalAlign() })} onClick={() => handleAlignment('vertical', 'start')} disabled = {disableHorizontalAlign()}>
          <i className="cleverstory-widgets-align-left" />
          <span>Align to left</span>
        </button>
        <button className={cx(styles.action, { [styles.actionDisabled]: disableHorizontalAlign() })} onClick={() => handleAlignment('vertical', 'center')}disabled = {disableHorizontalAlign()} >
          <i className="cleverstory-widgets-align-center" />
          <span>Align to center</span>
        </button>
        <button className={cx(styles.action, { [styles.actionDisabled]: disableHorizontalAlign() })} onClick={() => handleAlignment('vertical', 'end')} disabled = {disableHorizontalAlign()}>
          <i className="cleverstory-widgets-align-right" />
          <span>Align to right</span>
        </button>
        <button className={cx(styles.action, { [styles.actionDisabled]: disableVerticalAlign() })} onClick={() => handleAlignment('horizontal', 'start')} disabled = {disableVerticalAlign()}>
          <i className="cleverstory-widgets-align-top" />
          <span>Align to top</span>
        </button>
        <button className={cx(styles.action, { [styles.actionDisabled]: disableVerticalAlign() })} onClick={() => handleAlignment('horizontal', 'center')} disabled = {disableVerticalAlign()}>
          <i className="cleverstory-widgets-align-center-vertical" />
          <span>Align to middle</span>
        </button>
        <button className={cx(styles.action, { [styles.actionDisabled]: disableVerticalAlign() })} onClick={() => handleAlignment('horizontal', 'end')} disabled = {disableVerticalAlign()}>
          <i className="cleverstory-widgets-align-bottom" />
          <span>Align to bottom</span>
        </button>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="right-start"
      rootClose
      overlay={!disabled() ? popover : (<></>)}
    >
      <span className={cx({ [styles.actionDisabled]: disabled() })}>
        <ToolAction
          icon='cleverstory-widgets-align-center'
          content='Align'
          disabled={disabled() || isAllWidgetLocked}
        />
      </span>
    </OverlayTrigger>
  );
}

const MatchSizeTool = ({isAllWidgetLocked}) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    selectedId: ''
  })

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const widgets = useEditableWidgets(editorSession);
  const selected = useSelector(state => _.get(state, `${editorSession}.present.selected`));

  useEffect(() => {
    if (_.size(selected) === 1) {
      setState(state => {
        return {
          ...state,
          selectedId: _.head(selected)
        }
      })
    }
  }, [selected])

  const handleSize = (type) => {
    const moveableManager = editorContext.editorSession.moveableManager.current;
    if (!moveableManager.props.resizable) {
      return;
    }
  
    actionGroup.start();
  
    let selectedWidget = widgets.filter(widget => widget.uid === state.selectedId);
  
    const refLayout = _.get(selectedWidget[0], `properties.${breakpoint}.layout`);
    const width = _.get(refLayout, 'width');
    const height = _.get(refLayout, 'height');
  
    widgets.filter(widget => widget.uid !== state.selectedId).forEach(widget => {
      const widgetConfig = WidgetRegistry.moveableConfig(editorSession, widget.type);
      var layout = _.get(widget, `properties.${breakpoint}.layout`);
  
      if (type === 'height') {
        layout = { ...layout, height }
      }
      if (type === 'width') {
        layout = { ...layout, width }
      }
      if (type === 'match') {
        layout = { ...layout, width, height }
      }
  
      if (widgetConfig && widgetConfig.onResize) {
        widgetConfig.onResize(widget, layout, breakpoint);
      }
  
      dispatch(updateWidgetLayout(editorSession, widget.uid, breakpoint, layout));
    });
  
    actionGroup.end();
  }

  const disabled = () => {
    if (_.size(widgets) < 2) {
      return true;
    }

    return widgets.some(item => item.type === GROUP);
  }

  const popover = (
    <Popover
      id="popover-arrange-tool"
      className={styles.arrangeToolPopup}
    >
      <Popover.Body>
        <button className={styles.action} onClick={() => handleSize('width')}>
          <i className="cleverstory-match-width" />
          <span>Match width</span>
        </button>
        <button className={styles.action} onClick={() => handleSize('match')} >
          <i className="cleverstory-match" />
          <span>Match</span>
        </button>
        <button className={styles.action} onClick={() => handleSize('height')} >
          <i className="cleverstory-match-height" />
          <span>Match height</span>
        </button>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="right-start"
      rootClose
      overlay={popover}
    >
      <span className={cx({ [styles.actionDisabled]: disabled() })}>
        <ToolAction
          icon='cleverstory-match-height'
          content='Match Size'
          disabled={disabled() || isAllWidgetLocked}
        />
      </span>
    </OverlayTrigger>
  );
}

const DistributeEquallyTool = ({isAllWidgetLocked}) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const widgets = useEditableWidgets(editorSession);
  const selected = useSelector(state => _.get(state, `${editorSession}.present.selected`));
  var updatedPosition = null;

  const handleDistribute = (type) => {

    const distributeSpace = calculateSpace(type);
    const selectedWidget = sortWidgetsByPosition(type)

    if (selectedWidget.length > 2 && distributeSpace ) {
      
      selectedWidget.forEach((widget, index) => {

        if (index !== 0  ) {
          var currentlayout = _.get(widget, `properties.${breakpoint}.layout`);

          const newPosition = calculateNewPosition(selectedWidget[index - 1], widget, distributeSpace, type);

          currentlayout = {
            ...currentlayout,
            transform:{
              ...currentlayout.transform,
              translate:`${newPosition}`
            }
          }
          
          dispatch(updateWidgetLayout(editorSession, widget.uid, breakpoint, currentlayout));
          updatedPosition = currentlayout;
        }
      })
    }
  }

  const calculateNewPosition = (prevWidget, currentWidget, distributeSpace, type) => {

    if (!prevWidget || !currentWidget || !distributeSpace) {
      return;
    }
    let prevlayout = updatedPosition?updatedPosition:_.get(prevWidget, `properties.${breakpoint}.layout`);
    let currentlayout = _.get(currentWidget, `properties.${breakpoint}.layout`)

    const prevposition = getBoundingRectFromLayout(prevlayout)
    const currentposition = getBoundingRectFromLayout(currentlayout);

    prevlayout = getBoundingRectForWidget(editorSession,prevWidget);

    const positionX = (prevposition.x + prevlayout.width) + distributeSpace.x;
    const positionY = (prevposition.y + prevlayout.height) + distributeSpace.y

    if (type === 'horizontal') {
      return `${positionX}px,${currentposition.y}px`;
    }

    if (type === 'vertical') {
      return `${currentposition.x}px,${positionY}px`;
    }

  }

  const calculateSpace = (type) => {
    if (selected.length < 1) {
      return
    }
    
    const moveableManager = editorContext.editorSession.moveableManager.current;
    const rect = moveableManager.getRect();
    const groupWidth = _.get(rect, 'width', '');
    const groupHeight = _.get(rect, 'height', '');

    const widgetWidth = rect.children.reduce((init, obj) => { return init + obj.width }, 0);
    const widgetHeight = rect.children.reduce((init, obj) => { return init + obj.height }, 0);

    const horizontalSpace = Math.round( (groupWidth - widgetWidth) / (selected.length - 1) );
    const verticalSpace = Math.round( (groupHeight - widgetHeight) / (selected.length - 1) );

    if (groupWidth && widgetWidth && type === 'horizontal') {
      return {x : horizontalSpace,y:0}
    }

    if (groupHeight && widgetHeight && type === 'vertical') {
      return {x : 0, y : verticalSpace}
    }

  }


  const sortWidgetsByPosition = (type) => {
     if( selected.length > 1) {
       var widgetSelected = widgets.filter((widget) => {
         return selected.some((element) => {
           return element === widget.uid;
         });
       });
       
      const sortedResult = widgetSelected.sort((prev,next) => {
        var Prevlayout = _.get(prev, `properties.${breakpoint}.layout`);
        var Nextlayout = _.get(next, `properties.${breakpoint}.layout`);
        const Prevposition = getBoundingRectFromLayout(Prevlayout);
        const Nextposition = getBoundingRectFromLayout(Nextlayout);

        if(type === 'horizontal') {
          return parseFloat(Prevposition.x) - parseFloat(Nextposition.x);     
        }

        if(type === 'vertical') {
          return parseFloat(Prevposition.y) - parseFloat(Nextposition.y);     
        }

      });

      return sortedResult;  
     }
  }

  const popover = (
    <Popover
      id="popover-arrange-tool"
      className={styles.arrangeToolPopup}
    >
      <Popover.Body>
        <button className={styles.action} onClick={() => handleDistribute('vertical')} >
          <i className="cleverstory-distribute-vertically" />
          <span>Distribute vertical</span>
        </button>
        <button className={styles.action} onClick={() => handleDistribute('horizontal')} >
          <i className="cleverstory-distribute-horizontally" />
          <span>Distribute horizontal</span>
        </button>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="right-start"
      rootClose
      overlay={popover}
    >
      <span className={cx({[styles.actionDisabled]:_.size(widgets) < 2})}>
        <ToolAction
          icon='cleverstory-distribute-horizontally'
          content='Distribute Equally'
          disabled={_.size(widgets) < 2 || isAllWidgetLocked}
        />
      </span>
    </OverlayTrigger>
  );
}

/**
 * Common Components
*/
const ToolAction = ({ content, icon, disabled, onClick }) => {
  return (
    <Tooltip className={styles.tooltip} placement='top' content={content}>
      <Button variant="info" disabled={disabled} onClick={onClick}>
        <i className={icon} />
      </Button>
    </Tooltip>
  )
}

const NumericInput = ({ className, min, max, step = 1, disabled, value, onChange, id }) => {
  const [input, setInput] = useState(value);

  useEffect(() => {
    setInput(value);
  }, [value]);

  const handleChange = (value) => {
    if (onChange) {
      onChange(value);
    }
  }

  const processChange = (value) => {
    if (min != null) {
      value = Math.max(min, value);
    }

    if (max != null) {
      value = Math.min(max, value);
    }

    handleChange(value);
  }

  const handleInputChange = (e) => {
    setInput(e.target.value);
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processChange(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var newValue = parseInt(input) + step;
      newValue = max ? Math.min(max, newValue) : newValue;
      processChange(newValue);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      var newValue = parseInt(input) - step;
      newValue = min ? Math.max(min, newValue) : newValue;
      processChange(newValue);
    }
  }

  const handleOnBlur = () => {
    processChange(input);
  }

  return (
    <input
      id={id}
      type='text'
      disabled={disabled}
      value={input}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onBlur={handleOnBlur}
    />
  );
}

/**
 * Hooks
 */
export const useEditableWidget = (editorSession) => {
  const widget = useSelector(state => {
    const selected = _.get(state, `${editorSession}.present.selected`);

    if (_.size(selected) != 1) {
      return null;
    }

    const id = selected[0];
    const widgets = _.get(state, `${editorSession}.present.widgets`);

    var widget = widgets.find(widget => widget.uid === id);
    if (!widget) {
      return null;
    }

    if (widget.type === PAGE) {
      return null;
    }

    return widget;
  });

  return widget;
}

export const useEditableWidgets = (editorSession) => {
  const widgets = useSelector(state => {
    const selected = _.get(state, `${editorSession}.present.selected`) || [];

    const widgets = _.get(state, `${editorSession}.present.widgets`);

    return selected.map(id => {
      var widget = widgets.find(widget => widget.uid === id);
      if (!widget) {
        return null;
      }

      if (widget.type === PAGE || widget.type === ANCHOR) {
        return null;
      }
      return widget;
    }).filter(widget => widget != null);
  });

  return widgets;
}